'use client';

import { useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface SimConfig {
  homeTeam: string;
  awayTeam: string;
  homeGoalsPerGame: number;
  awayGoalsPerGame: number;
  homeGoalsAgainst: number;
  awayGoalsAgainst: number;
  iterations: number;
}

interface SimResult {
  homeWin: number;
  awayWin: number;
  overtime: number;
  avgHomeGoals: number;
  avgAwayGoals: number;
  scoreDist: { score: string; prob: number }[];
  periodProbs: { period: string; homeLeading: number; tied: number; awayLeading: number }[];
  goalDist: { goals: number; homeProb: number; awayProb: number }[];
  volatility: number;
  confidence: number;
  topScenarios: { scenario: string; prob: number; ev: number }[];
}

const NHL_TEAMS = [
  'Boston Bruins', 'Toronto Maple Leafs', 'Tampa Bay Lightning', 'Florida Panthers',
  'Colorado Avalanche', 'Vegas Golden Knights', 'Edmonton Oilers', 'Carolina Hurricanes',
  'New York Rangers', 'Dallas Stars', 'Minnesota Wild', 'Los Angeles Kings',
];

function runMonteCarlo(config: SimConfig): SimResult {
  const { homeGoalsPerGame, awayGoalsPerGame, homeGoalsAgainst, awayGoalsAgainst, iterations } = config;

  const homeExpected = (homeGoalsPerGame + awayGoalsAgainst) / 2 * 1.05;
  const awayExpected = (awayGoalsPerGame + homeGoalsAgainst) / 2;

  const poissonRandom = (lambda: number): number => {
    const L = Math.exp(-lambda);
    let k = 0, p = 1;
    do { k++; p *= Math.random(); } while (p > L);
    return k - 1;
  };

  let homeWins = 0, awayWins = 0, overtimes = 0;
  let totalHomeGoals = 0, totalAwayGoals = 0;
  const scoreMap: Record<string, number> = {};
  const periodData = [
    { homeLeading: 0, tied: 0, awayLeading: 0 },
    { homeLeading: 0, tied: 0, awayLeading: 0 },
    { homeLeading: 0, tied: 0, awayLeading: 0 },
  ];
  const homeGoalDist: number[] = new Array(9).fill(0);
  const awayGoalDist: number[] = new Array(9).fill(0);

  for (let i = 0; i < iterations; i++) {
    const p1Home = poissonRandom(homeExpected / 3);
    const p1Away = poissonRandom(awayExpected / 3);
    const p2Home = poissonRandom(homeExpected / 3);
    const p2Away = poissonRandom(awayExpected / 3);
    const p3Home = poissonRandom(homeExpected / 3);
    const p3Away = poissonRandom(awayExpected / 3);

    const hg = p1Home + p2Home + p3Home;
    const ag = p1Away + p2Away + p3Away;

    totalHomeGoals += hg;
    totalAwayGoals += ag;

    homeGoalDist[Math.min(hg, 8)]++;
    awayGoalDist[Math.min(ag, 8)]++;

    // Period snapshots
    const afterP1H = p1Home, afterP1A = p1Away;
    const afterP2H = p1Home + p2Home, afterP2A = p1Away + p2Away;
    periodData[0][afterP1H > afterP1A ? 'homeLeading' : afterP1H < afterP1A ? 'awayLeading' : 'tied']++;
    periodData[1][afterP2H > afterP2A ? 'homeLeading' : afterP2H < afterP2A ? 'awayLeading' : 'tied']++;
    periodData[2][hg > ag ? 'homeLeading' : hg < ag ? 'awayLeading' : 'tied']++;

    const key = `${hg}-${ag}`;
    scoreMap[key] = (scoreMap[key] || 0) + 1;

    if (hg > ag) homeWins++;
    else if (ag > hg) awayWins++;
    else {
      overtimes++;
      if (Math.random() > 0.5) homeWins++;
      else awayWins++;
    }
  }

  const scoreDist = Object.entries(scoreMap)
    .map(([score, count]) => ({ score, prob: Math.round((count / iterations) * 1000) / 10 }))
    .sort((a, b) => b.prob - a.prob)
    .slice(0, 10);

  const periodProbs = periodData.map((p, i) => ({
    period: `P${i + 1}`,
    homeLeading: Math.round((p.homeLeading / iterations) * 100),
    tied: Math.round((p.tied / iterations) * 100),
    awayLeading: Math.round((p.awayLeading / iterations) * 100),
  }));

  const goalDist = Array.from({ length: 9 }, (_, g) => ({
    goals: g,
    homeProb: Math.round((homeGoalDist[g] / iterations) * 100),
    awayProb: Math.round((awayGoalDist[g] / iterations) * 100),
  }));

  const homeWinPct = homeWins / iterations;
  const awayWinPct = awayWins / iterations;
  const otPct = overtimes / iterations;
  const volatility = Math.round((1 - Math.abs(homeWinPct - 0.5) * 2) * 100);
  const confidence = Math.round(Math.max(homeWinPct, awayWinPct) * 100);

  const topScenarios = [
    { scenario: `${config.homeTeam} wins regulation`, prob: Math.round((homeWins - overtimes * 0.5) / iterations * 100), ev: 1.2 },
    { scenario: `${config.awayTeam} wins regulation`, prob: Math.round((awayWins - overtimes * 0.5) / iterations * 100), ev: 1.8 },
    { scenario: 'Game goes to OT/SO', prob: Math.round(otPct * 100), ev: 2.1 },
    { scenario: 'Over 5.5 goals', prob: Math.round(goalDist.slice(6).reduce((s, g) => s + g.homeProb, 0) / 2), ev: 1.9 },
  ];

  return {
    homeWin: Math.round(homeWinPct * 100),
    awayWin: Math.round(awayWinPct * 100),
    overtime: Math.round(otPct * 100),
    avgHomeGoals: Math.round((totalHomeGoals / iterations) * 10) / 10,
    avgAwayGoals: Math.round((totalAwayGoals / iterations) * 10) / 10,
    scoreDist,
    periodProbs,
    goalDist,
    volatility,
    confidence,
    topScenarios,
  };
}

export default function SimulationLabClient() {
  const [config, setConfig] = useState<SimConfig>({
    homeTeam: 'Boston Bruins',
    awayTeam: 'Toronto Maple Leafs',
    homeGoalsPerGame: 3.2,
    awayGoalsPerGame: 3.0,
    homeGoalsAgainst: 2.6,
    awayGoalsAgainst: 2.8,
    iterations: 10000,
  });
  const [result, setResult] = useState<SimResult | null>(null);
  const [running, setRunning] = useState(false);

  const runSimulation = useCallback(() => {
    setRunning(true);
    setTimeout(() => {
      const res = runMonteCarlo(config);
      setResult(res);
      setRunning(false);
    }, 600);
  }, [config]);

  const update = (key: keyof SimConfig, value: string | number) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
            <Icon name="BeakerIcon" size={20} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">SimulationLab</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Monte Carlo Engine · {config.iterations.toLocaleString()} Iterations</p>
          </div>
        </div>
        <button
          onClick={runSimulation}
          disabled={running}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-semibold transition-all"
        >
          {running ? (
            <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Simulation en cours...</>
          ) : (
            <><Icon name="PlayIcon" size={16} /> Lancer la Simulation</>
          )}
        </button>
      </div>

      {/* Config */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Home team */}
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-positive" />
            <span className="text-sm font-semibold text-foreground">Équipe Domicile</span>
          </div>
          <select value={config.homeTeam} onChange={e => update('homeTeam', e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
            {NHL_TEAMS.map(t => <option key={t}>{t}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-muted-foreground font-mono-data block mb-1">BUTS/MATCH</label>
              <input type="number" step="0.1" min="1" max="6" value={config.homeGoalsPerGame}
                onChange={e => update('homeGoalsPerGame', parseFloat(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-2xs text-muted-foreground font-mono-data block mb-1">BUTS ENCAISSÉS</label>
              <input type="number" step="0.1" min="1" max="6" value={config.homeGoalsAgainst}
                onChange={e => update('homeGoalsAgainst', parseFloat(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
          </div>
        </div>

        {/* Away team */}
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 rounded-full bg-negative" />
            <span className="text-sm font-semibold text-foreground">Équipe Visiteur</span>
          </div>
          <select value={config.awayTeam} onChange={e => update('awayTeam', e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
            {NHL_TEAMS.map(t => <option key={t}>{t}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-muted-foreground font-mono-data block mb-1">BUTS/MATCH</label>
              <input type="number" step="0.1" min="1" max="6" value={config.awayGoalsPerGame}
                onChange={e => update('awayGoalsPerGame', parseFloat(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-2xs text-muted-foreground font-mono-data block mb-1">BUTS ENCAISSÉS</label>
              <input type="number" step="0.1" min="1" max="6" value={config.awayGoalsAgainst}
                onChange={e => update('awayGoalsAgainst', parseFloat(e.target.value))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
          </div>
        </div>
      </div>

      {/* Iterations */}
      <div className="bg-card border border-border rounded-xl p-4">
        <label className="text-2xs text-muted-foreground font-mono-data block mb-2">ITÉRATIONS MONTE CARLO</label>
        <div className="flex gap-2">
          {[1000, 5000, 10000, 50000].map(n => (
            <button key={n} onClick={() => update('iterations', n)}
              className={`px-4 py-2 rounded-lg text-sm font-mono-data transition-all ${config.iterations === n ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:border-primary/40'}`}>
              {n.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Win probabilities */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-positive/30 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-positive font-mono-data">{result.homeWin}%</div>
              <div className="text-xs text-muted-foreground mt-1">{config.homeTeam}</div>
              <div className="text-2xs text-positive font-mono-data">VICTOIRE</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-muted-foreground font-mono-data">{result.overtime}%</div>
              <div className="text-xs text-muted-foreground mt-1">Prolongation</div>
              <div className="text-2xs text-muted-foreground font-mono-data">OT/SO</div>
            </div>
            <div className="bg-card border border-negative/30 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-negative font-mono-data">{result.awayWin}%</div>
              <div className="text-xs text-muted-foreground mt-1">{config.awayTeam}</div>
              <div className="text-2xs text-negative font-mono-data">VICTOIRE</div>
            </div>
          </div>

          {/* Metrics row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-foreground font-mono-data">{result.avgHomeGoals}</div>
              <div className="text-2xs text-muted-foreground">Buts Moy. DOM</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-foreground font-mono-data">{result.avgAwayGoals}</div>
              <div className="text-2xs text-muted-foreground">Buts Moy. VIS</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-yellow-400 font-mono-data">{result.volatility}%</div>
              <div className="text-2xs text-muted-foreground">Volatilité</div>
            </div>
            <div className="bg-card border border-border rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-primary font-mono-data">{result.confidence}%</div>
              <div className="text-2xs text-muted-foreground">Confiance</div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Score distribution */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="ChartBarIcon" size={14} className="text-primary" />
                Distribution des Scores
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={result.scoreDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="score" tick={{ fontSize: 10, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} unit="%" />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} formatter={(v: number) => [`${v}%`, 'Probabilité']} />
                  <Bar dataKey="prob" fill="#00D4FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Period probabilities */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="ClockIcon" size={14} className="text-primary" />
                Probabilités Période par Période
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={result.periodProbs}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} unit="%" />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                  <Bar dataKey="homeLeading" name="DOM Mène" fill="#22c55e" stackId="a" />
                  <Bar dataKey="tied" name="Égalité" fill="#888" stackId="a" />
                  <Bar dataKey="awayLeading" name="VIS Mène" fill="#ef4444" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Goal distribution */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="ArrowTrendingUpIcon" size={14} className="text-primary" />
                Distribution des Buts
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={result.goalDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="goals" tick={{ fontSize: 10, fill: '#888' }} label={{ value: 'Buts', position: 'insideBottom', offset: -2, fill: '#888', fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} unit="%" />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                  <Line type="monotone" dataKey="homeProb" name="Domicile" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="awayProb" name="Visiteur" stroke="#ef4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Top scenarios */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="LightBulbIcon" size={14} className="text-primary" />
                Scénarios Principaux
              </h3>
              <div className="space-y-2">
                {result.topScenarios.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-background border border-border">
                    <span className="text-sm text-foreground">{s.scenario}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-muted rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-primary" style={{ width: `${s.prob}%` }} />
                      </div>
                      <span className="text-sm font-mono-data text-primary w-10 text-right">{s.prob}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!result && !running && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="BeakerIcon" size={48} className="text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground text-sm">Configurez les paramètres et lancez la simulation Monte Carlo</p>
          <p className="text-muted-foreground/60 text-xs mt-1 font-mono-data">10 000 ITÉRATIONS · DISTRIBUTIONS PROBABILISTES · VARIANCE MATCH</p>
        </div>
      )}
    </div>
  );
}
